// src/context/AuthContext.jsx
// Holds the session. On mount, if an access token exists, hydrates the user
// from /auth/me (which returns role + tier). Listens for "auth:logout" emitted
// by the client when a refresh fails, and drops the session.

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import * as authApi from "../api/auth";
import { tokenStore } from "../api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // restore an existing session on first load
  useEffect(() => {
    let active = true;
    (async () => {
      if (!tokenStore.access) { setIsLoading(false); return; }
      try {
        const profile = await authApi.me();
        if (active) setUser(profile);
      } catch (e) {
        tokenStore.clear();
      } finally {
        if (active) setIsLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // client emits this when refresh fails
  useEffect(() => {
    const onLogout = () => setUser(null);
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, []);

  const login = useCallback(async (email, password) => {
    await authApi.login(email, password);
    // login returns a trimmed user; re-read /auth/me for the full profile (tier etc)
    const profile = await authApi.me();
    setUser(profile);
    return profile;
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
